import * as React from "react";
import styled, { css } from "styled-components";

import { theme } from "src/library/utils";

import { Text } from "../Text/Text";

interface TabbedNavigationProps extends Pick<React.HTMLAttributes<HTMLDivElement>, "className"> {
  tabs: TabOptions[];
  defaultTab?: string;
  // allows consumer to control the active tab as changes to default tab updates active tab
  amendDefaultTab?: (name: string) => void;
}

export interface TabOptions {
  title: string | JSX.Element;
  content: () => React.ReactElement;
  name?: string;
  pad?: TabContentPaddingProps;
}

interface TabContentPaddingProps {
  top?: string | number;
  bottom?: string | number;
  left?: string | number;
  right?: string | number;
}

interface StyledTabContentProps {
  pad?: TabContentPaddingProps;
}

/**
 * Tab navigation component
 */
export const TabbedNavigation: React.ReactFCWithChildren<TabbedNavigationProps> = React.memo(({
  defaultTab, tabs, amendDefaultTab, ...rest
}) => {
  const [ activeTab, setActiveTab ] = React.useState(0);

  // On defaultTab change
  React.useEffect(() => {
    // If a defaultTab is provided
    if (defaultTab) {
      for (const i in tabs) {
        const tab = tabs[ i ];

        // If the tab has a name and it matches the defaultTab, update the activeTab state
        if (tab.name === defaultTab) {
          setActiveTab(parseInt(i));
          break;
        }
      }
    }
  }, [ defaultTab, tabs ]);

  return (
    <TabbedNavigationWrapper {...rest}>
      <div className="tab-options">
        {tabs.map((tab, i) => (
          <div
            key={`tab-option-${i}`}
            className={`tab-option ${i === activeTab ? "active" : ""} `}
            onClick={() => {
              setActiveTab(i);
              amendDefaultTab && amendDefaultTab(tab.name || i.toString());
            }}
          >
            {typeof tab.title === "string" ? (
              <Text>
                {tab.title}
              </Text>
            ) : tab.title}
          </div>
        ))}
      </div>

      {!!tabs[ activeTab ] && (
        <StyledTabContent
          key={`tab-${activeTab}`}
          className="tab-content"
          pad={tabs[ activeTab ].pad}
        >
          {!!tabs[ activeTab ] && tabs[ activeTab ].content()}
        </StyledTabContent>
      )}
    </TabbedNavigationWrapper>
  );
});

const TabbedNavigationWrapper = styled.div`
  ${({ theme }) => css`
    .tab-options {
      display: flex;
      position: relative;

      &::after {
        content: "";
        height: 0.1rem;
        width: 100%;
        position: absolute;
        bottom: 0;
        left: 0;
        z-index: -1;
        background: ${theme.colors.lightGrey};
      }

      .tab-option {
        border-bottom: 0.1rem solid ${theme.colors.lightGrey};
        padding: ${theme.spacing.padding.sm} ${theme.spacing.padding.md};
        border-top: 0.1rem solid ${theme.colors.lightGrey};
        border-left: 0.1rem solid ${theme.colors.lightGrey};
        cursor: pointer;
        position: relative;
        background: ${theme.colors.lightestGrey};

        :last-child {
          border-right: 0.1rem solid ${theme.colors.lightGrey};
        }

        &::before {
          content: "";
          height: 0.4rem;
          width: 100%;
          left: 0;
          position: absolute;
          top: 0;
          background: ${theme.colors.primaryDark};
          z-index: -1;
          transition: top ease-in-out 0.4s;
        }

        &.active {
          border-bottom: 0.1rem solid ${theme.colors.white};
          background: ${theme.colors.white};
          border-top: 0.1rem solid ${theme.colors.white};

          &::before {
            top: -0.5rem;
          }
        }

        :not(.active):hover {
          &::before {
            background: ${theme.colors.primaryLight};
            top: -0.5rem;
          }
        }
      }
    }
  `}
`;

const StyledTabContent = styled.div<StyledTabContentProps>`
  ${({ theme, pad }) => css`
    margin-bottom: ${theme.spacing.margins.md};
    background: ${theme.colors.white};
    border: 0.1rem solid ${theme.colors.lightGrey};
    border-top: none;
    ${setTabContentPaddingCss(pad)};
  `};
`;

const setTabContentPaddingCss = (pad?: TabContentPaddingProps) => {
  let padding: TabContentPaddingProps = {
    top: theme.spacing.padding.lg,
    bottom: theme.spacing.padding.lg,
    left: theme.spacing.padding.md,
    right: theme.spacing.padding.md
  };

  // If a padding is provided, merge the given pad with the default set above
  if (pad) {
    padding = {
      ...padding,
      ...pad
    };
  }

  return css`
    padding-top: ${padding.top};
    padding-bottom: ${padding.bottom};
    padding-left: ${padding.left};
    padding-right: ${padding.right};
  `;
};
